import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Divider, Paper, Typography } from '@mui/material';
import ViewCarouselOutlinedIcon from '@mui/icons-material/ViewCarouselOutlined';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import { getProductModules } from '../../../services/ecommerceApi';
import type { CustomModule } from '../ECommercePage';
import HomePageContentCardsPanel from './HomePageContentCardsPanel';
import HomePageContentTagsPanel from './HomePageContentTagsPanel';

interface ContentCardsSettingsProps {
  companyId: string;
  configId: string;
}

const ContentCardsSettings: React.FC<ContentCardsSettingsProps> = ({ companyId, configId }) => {
  const [homepageModules, setHomepageModules] = useState<CustomModule[]>([]);

  useEffect(() => {
    if (!companyId) return;

    getProductModules(companyId)
      .then((response) => {
        const rows = Array.isArray(response?.data) ? response.data : [];
        const modules: CustomModule[] = rows
          .filter((row: any) => {
            const type = String(row?.moduleType || '').toLowerCase();
            return type !== 'content-cards' && type !== 'content-tags' && type !== 'faq-section';
          })
          .map((row: any) => ({
            id: row.id,
            tabName: row.tabName || row.sectionTitle || 'Modül',
            sectionTitle: row.sectionTitle || row.tabName || 'Modül',
            order: Number(row.displayOrder) || 0,
            settings: row.settingsJson ? JSON.parse(row.settingsJson) : {}
          }));
        setHomepageModules(modules);
      })
      .catch((error) => {
        console.error('[CONTENT CARDS SETTINGS] Ana sayfa modülleri yüklenemedi:', error);
        setHomepageModules([]);
      });
  }, [companyId]);

  const processedModules = useMemo(
    () =>
      homepageModules.filter(
        (module) =>
          module.tabName !== 'İçerik Kartları' &&
          module.tabName !== 'İçerik Etiketleri' &&
          String(module.id || '').length > 0
      ),
    [homepageModules]
  );

  if (!configId) {
    return (
      <Paper elevation={3} sx={{ p: 3 }}>
        <Alert severity="warning">
          <Typography variant="h6" gutterBottom>
            E-ticaret yapılandırması bulunamadı
          </Typography>
          <Typography variant="body2">
            Kart yönetimini kullanmak için önce Genel Ayarlar sekmesinden e-ticaret yapılandırmasını kaydedin.
          </Typography>
        </Alert>
      </Paper>
    );
  }

  return (
    <Paper elevation={3} sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <ViewCarouselOutlinedIcon color="primary" />
        <Typography variant="h5" fontWeight={700}>
          Kartlar
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Bir kart şablonu seçin; ana sayfaya, Hakkımızda / Hizmetler / Ürün ve Çözümler / Referanslar
        sekmelerine ve özel sayfalara birden fazla kart bloğu ekleyin. Tüm bloklar aşağıda sayfalarına göre
        listelenir; buradan düzenleyebilir, gizleyebilir veya silebilirsiniz.
      </Typography>

      <HomePageContentCardsPanel
        companyId={companyId}
        configId={configId}
        homepageModules={processedModules}
      />

      <Divider sx={{ my: 4 }} />

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <LocalOfferOutlinedIcon color="primary" />
        <Typography variant="h5" fontWeight={700}>
          Etiketler
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Kartlardan bağımsız, renkli pill görünümlü etiket blokları ekleyin (örn. "Yan Donanım"). Aynı sayfa
        ve konum seçenekleriyle ana sayfaya veya diğer sayfalara yerleştirilir.
      </Typography>

      <HomePageContentTagsPanel
        companyId={companyId}
        configId={configId}
        homepageModules={processedModules}
      />
    </Paper>
  );
};

export default ContentCardsSettings;
